import { Permissions } from '../../../models/enums/permissions';
import { ClickAwayListener, Tab, Tabs } from '@material-ui/core';
import cloneDeep from 'lodash/cloneDeep';
import * as React from 'react';
import { FlatButton } from '../../../components/button/FlatButton';
import { PlaceholderBlock } from '../../../components/utility/PlaceholderBlock';
import { HasPermission } from '../../../containers/authorization-helpers/HasPermission';
import { StatusCode } from '../../../utils/api';
import { RouteStatus } from '../../models/enums/_index';
import { ShipmentActionType } from '../../models/enums/shipment-action-type';
import { ShipmentType } from '../../models/enums/shipment-type';
import { OrderSummary } from '../../models/shared/order-summary';
import { RunningTotal } from '../../models/shared/running-total';
import { BackhaulShipmentAction, DeliveryShipmentAction, EndShipmentAction, ShipmentAction, StartShipmentAction } from '../../models/shipments/shipment-action';
import { ShipmentRead } from '../../models/shipments/shipment-read';
import { TruckRead } from '../../models/trucks/truck-read';
import { ActionAddActivity } from './actions/ActionAddActivity';
import { ActionContentBackHaul } from './actions/ActionContentBackHaul';
import { ActionContentCustomerDelivery } from './actions/ActionContentCustomerDelivery';
import { ActionContentRouteEnd } from './actions/ActionContentRouteEnd';
import { ActionContentRouteStart } from './actions/ActionContentRouteStart';
import { ActionEditBackhaul } from './actions/ActionEditBackhaul';
import { ActionEditCustomerDelivery } from './actions/ActionEditCustomerDelivery';
import { ActionEditDeliveryAddress } from './actions/ActionEditDeliveryAddress';
import { ActionSetCOD } from './actions/ActionSetCOD';
import { ActionSetSchedule } from './actions/ActionSetSchedule';
import { ActionSetTruckCustoms } from './actions/ActionSetTruckCustoms';
import { BackhaulShipmentTab, CustomerDeliverShipmentTab, DestinationShipmentTab, OriginShipmentTab } from './ShipmentTab';
import { ActionAddAddon } from './actions/ActionAddAddon';
import { ActionAddBackorder } from './actions/ActionAddBackorder';
import { BackorderStatusFlyout } from '../../../modules/ordering/components/BackOrderStatusFlyout';
import ResultDialog from '../../../components/dialog-types/ResultDialog';


interface IProps {
    route               : ShipmentRead;
    markStopDelivered   : any;
    deleteStop          : any;
    refreshRoute        : any;
}

interface ITabPanelProps {
    children?   : React.ReactNode;
    index       : any;
    value       : any;
}

const TabPanel = (props: ITabPanelProps) => {
    const { children, value, index } = props;
    return value === index 
            ? <div className="truck-delivery-row">{children}</div> 
            : null;
};

export const EditShipmentActionList = (props: IProps) => {

    const [selectedRoute, setSelectedRoute] = React.useState<number>(0);
    const [isActionsOpen, setIsActionsOpen] = React.useState<boolean>(false);
    const [isAddingActivity, setIsAddingActivity] = React.useState<boolean>(false);
    const [isSettingCustoms, setIsSettingCustoms] = React.useState<boolean>(false);
    const [isSettingSchedule, setIsSettingSchedule] = React.useState<boolean>(false);
    const [isAddingAddon, setIsAddingAddon] = React.useState<boolean>(false);
    const [isAddingBackorder, setIsAddingBackorder] = React.useState<boolean>(false);
    const [isBackorderInProgress, setIsBackorderInProgress] = React.useState<boolean>(false);
    const [isSettingCOD, setIsSettingCOD] = React.useState<boolean>(false);
    const [isEditingDelivery, setIsEditingDelivery] = React.useState<boolean>(false);
    const [isEditingDeliveryAddress, setIsEditingDeliveryAddress] = React.useState<boolean>(false);
    const [isEditingBackhaul, setIsEditingBackhaul] = React.useState<boolean>(false);
    const [backorderIds, setBackorderIds] = React.useState<string>("");

    const [editingTruck, setEditingTruck] = React.useState<TruckRead | undefined>(undefined);
    const [editingStopList, setEditingStopList] = React.useState<ShipmentAction[]>([]);
    const [editingDelivery, setEditingDelivery] = React.useState<DeliveryShipmentAction | undefined>(undefined);
    const [editingBackhaul, setEditingBackhaul] = React.useState<BackhaulShipmentAction | undefined>(undefined);
    const [truckTotals, setTruckTotals] = React.useState<RunningTotal | undefined>(undefined);

    const onSelectedRouteChange = (event: React.ChangeEvent<{}>, newIdx: any) => {
        setSelectedRoute(newIdx);
    };

    const deleteStop = async (itm: ShipmentAction) => {
        const result = await props.deleteStop(itm.id);
        if (result === StatusCode.Success) {
            setSelectedRoute(0);
        }
    };

    /**
     * Route Option List Handlers
     */
    const toggleActionsList = () => {
        setIsActionsOpen(!isActionsOpen);
    };

    const hideActionsList = () => {
        setIsActionsOpen(false);
    };

    /**
     * Flyout Show Handlers
     */
    const showAddActivityFlyout = () => {
        const newTotals = updateRunningTotals(undefined);
        setTruckTotals(newTotals);
        setIsAddingActivity(true);
    };

    const showScheduleFlyout = () => {
        const listCopy = cloneDeep(props.route.actions);
        setEditingStopList(listCopy);
        setIsSettingSchedule(true);
    };

    const showCODFlyout = () => {
        const listCopy = cloneDeep(props.route.actions);
        setEditingStopList(listCopy);
        setIsSettingCOD(true);
    };

    const showContainerCustomsFlyout = () => {
        const routeCopy = cloneDeep(props.route) as TruckRead;
        setEditingTruck(routeCopy);
        setIsSettingCustoms(true);
    };

    /**
     * Load a customer delivery for editing and show it in the flyout
     * @param stopId The customer delivery to load
     */
    const showEditDeliveryFlyout = (stopId: number) => {

        const toEdit = props.route.actions.find((itm: ShipmentAction) => {
            return itm.id === stopId;
        });

        if (toEdit) {
            const x = cloneDeep(toEdit);

            const newTotals = updateRunningTotals(stopId);
            setTruckTotals(newTotals);
            setEditingDelivery(x as DeliveryShipmentAction);
            setIsEditingDelivery(true);
        }
        else {
            console.log('Invalid stop ID provided.');
        }
    };

    const showAddAddonFlyout = (stopId: number) => {
        const toEdit = props.route.actions.find((itm: ShipmentAction) => {
            return itm.id === stopId;
        });

        if (toEdit) {
            const x = cloneDeep(toEdit);

            // The add-on lands on top of every stop already on the truck
            setTruckTotals(updateRunningTotals(undefined));
            setEditingDelivery(x as DeliveryShipmentAction);
            setIsAddingAddon(true)
        }
        else {
            console.log('Invalid stop ID provided.');
        }
    }

    const showBackorderFlyout = (stopId: number) => {
        const toEdit = props.route.actions.find((itm: ShipmentAction) => {
            return itm.id === stopId;
        });

        if (toEdit) {
            const x = cloneDeep(toEdit);

            setEditingDelivery(x as DeliveryShipmentAction);
            setIsAddingBackorder(true)
        }
        else {
            console.log('Invalid stop ID provided.');
        }
    }

    /**
     * Load a customer delivery address for editing and show it in the flyout
     * @param stopId The customer delivery to load
     */
    const showEditDeliveryAddressFlyout = (stopId: number) => {

        const toEdit = props.route.actions.find((itm: ShipmentAction) => {
            return itm.id === stopId;
        });

        if (toEdit) {
            const x = cloneDeep(toEdit);
            setEditingDelivery(x as DeliveryShipmentAction);
            setIsEditingDeliveryAddress(true);
        }
        else {
            console.log('Invalid stop ID provided.');
        }
    };

    /**
     * Load a backhaul for editing and show it in the flyout
     * @param stopId The backhaul iD to load
     */
    const showEditBackhaulFlyout = (stopId: number) => {

        const toEdit = props.route.actions.find((itm: ShipmentAction) => {
            return itm.id === stopId;
        });

        if (toEdit) {
            const x = cloneDeep(toEdit);
            setEditingBackhaul(x as BackhaulShipmentAction);
            setIsEditingBackhaul(true);
        }
        else {
            console.log('Invalid stop ID provided.');
        }
    };

    /**
     * Flyout Hide Handlers
     */
    const hideAddActivityFlyout = () => {
        setIsAddingActivity(false);
    };

    const hideScheduleFlyout = () => {
        setEditingStopList([]);
        setIsSettingSchedule(false);
    };

    const hideCODFlyout = () => {
        setEditingStopList([]);
        setIsSettingCOD(false);
    };

    const hideEditDeliveryFlyout = () => {
        setEditingDelivery(undefined);
        setIsEditingDelivery(false);
    };

    const hideEditDeliveryAddressFlyout = () => {
        setEditingDelivery(undefined);
        setIsEditingDeliveryAddress(false);
    };

    const hideAddAddonFlyout = () => {
        setEditingDelivery(undefined);
        setIsAddingAddon(false);
    };

    const hideAddBackorderFlyout = () => {
        setEditingDelivery(undefined);
        setIsAddingBackorder(false);
    };

    const hideAddBackorderProgressFlyout = () => {  
        setBackorderIds("");
        setIsBackorderInProgress(false);
        props.refreshRoute();
    };

    const hideCustomsFlyout = () => {
        setEditingTruck(undefined);
        setIsSettingCustoms(false);
    };

    const hideEditBackhaulFlyout = () => {
        setEditingBackhaul(undefined);
        setIsEditingBackhaul(false);
    };

    /**
     * Flyout Success Handlers
     */
    const onAddActivitySuccess = () => {
        hideAddActivityFlyout();
        props.refreshRoute();
    };

    const onSetScheduleSuccess = () => {
        hideScheduleFlyout();
        props.refreshRoute();
    };

    const onSetCODAmount = () => {
        hideCODFlyout();
        props.refreshRoute();
    };

    const onEditStopSuccess = () => {
        hideEditDeliveryFlyout();
        props.refreshRoute();
    };

    const onEditDeliveryAddressBeforeSave = () => {
        hideEditDeliveryAddressFlyout();
    };

    const onEditDeliveryAddressSuccess = () => {
        hideEditDeliveryAddressFlyout();
        props.refreshRoute();
    };

    const onAddAddonSuccess = () => {
        hideAddAddonFlyout();
        props.refreshRoute();
    };

    const onAddBackorderSuccess = async (orderIds: string[]) => {

        hideAddBackorderFlyout();

        const result = await ResultDialog.show({
            title: 'Backorder Orders?',
            message: 'Are you sure you want to backorder these orders?  They will be removed from any pickups, deliveries and production runs they are currently assigned to.'
        });

        if (result) {
            var backorderIds = orderIds.join("\n");
            setBackorderIds(backorderIds);
            setIsBackorderInProgress(true);            
        }        
    };

    const onEditBackhaulSuccess = () => {
        hideEditBackhaulFlyout();
        props.refreshRoute();
    };

    const onSetCustomsSuccess = () => {
        hideCustomsFlyout();
        props.refreshRoute();
    };

    const updateRunningTotals = (stopId: number | undefined) => {
        // RunningTotal
        const tmpTruckTotal = new RunningTotal();
        props.route.actions
            .filter((stopItm: ShipmentAction) => {
                return (stopItm.type === ShipmentActionType.Delivery && stopItm.id !== stopId);
            })
            .forEach((deliveryItm: DeliveryShipmentAction) => {
                deliveryItm.orders.forEach((itm: OrderSummary) => {
                    tmpTruckTotal.units += itm.units;
                    tmpTruckTotal.weight += itm.weight;
                    tmpTruckTotal.singles += itm.singles;
                    tmpTruckTotal.sections += itm.sections;
                    tmpTruckTotal.track += itm.track;
                    tmpTruckTotal.hardware += itm.hardware;
                });
            });

        return tmpTruckTotal;
    };

    const canResendSchedules = () => {
        const isValid = props.route.actions.some((itm: ShipmentAction) => {
            return itm.type === ShipmentActionType.Delivery;
        });

        return (isValid && (props.route.status !== RouteStatus.Cancelled && props.route.status !== RouteStatus.Closed));
    };

    // UI Element Builders
    const getActionList = () => {
        if (props.route.type === ShipmentType.Truck) {
            return <div className="dropdown-menu show">
                <FlatButton
                    id="addActivityBtn"
                    text="Add Stop"
                    icon="la-plus-circle"
                    onBtnClick={showAddActivityFlyout}
                />
                <FlatButton
                    id="setScheduleBtn"
                    text="Set Schedule"
                    icon="la-calendar"
                    onBtnClick={showScheduleFlyout}
                />
                <FlatButton
                    id="setCODAmounts"
                    text="Set COD Amounts"
                    icon="la-money"
                    onBtnClick={showCODFlyout}
                />
                <FlatButton
                    id="setCustomsBtn"
                    text="Set Customs"
                    icon="la-globe"
                    onBtnClick={showContainerCustomsFlyout}
                />
            </div>;
        }
        else {
            return <div className="dropdown-menu show">
                <FlatButton
                    id="addActivityBtn"
                    text="Add Stop"
                    icon="la-plus-circle"
                    onBtnClick={showAddActivityFlyout}
                />
                <FlatButton
                    id="setScheduleBtn"
                    text="Set Schedule"
                    icon="la-calendar"
                    onBtnClick={showScheduleFlyout}
                />
            </div>;
        }
    };

    const getTabContent = (itm: ShipmentAction, index: number) => {

        if (itm.type === ShipmentActionType.Unknown) {
            return null;
        }

        switch (itm.type) {
            case ShipmentActionType.Delivery:
                return <CustomerDeliverShipmentTab
                    index={index}
                    delivery={itm as DeliveryShipmentAction}
                />;
            case ShipmentActionType.Backhaul:
                return <BackhaulShipmentTab
                    index={index}
                    backhaul={itm as BackhaulShipmentAction}
                />;
            case ShipmentActionType.Origin:
                return <OriginShipmentTab />;
            case ShipmentActionType.Destination:
                return <DestinationShipmentTab />;
            default:
                return null;
        }
    };

    const getTabPanelContent = (itm: ShipmentAction, index: number) => {

        if (index !== selectedRoute) {
            return;
        }

        switch (itm.type) {
            case ShipmentActionType.Delivery:
                return <ActionContentCustomerDelivery
                    key={itm.id}
                    delivery={itm as DeliveryShipmentAction}
                    routeStatus={props.route.status}
                    entityType={props.route.type}
                    onMarkDelivery={() => props.markStopDelivered(itm)}
                    onDeleteDelivery={() => deleteStop(itm)}
                    onStartEdit={showEditDeliveryFlyout}
                    onAddAddon={showAddAddonFlyout}
                    onAddBackorder={showBackorderFlyout}
                    onStartEditAddress={showEditDeliveryAddressFlyout}
                    serviceCenterId={props.route.serviceCenterId}
                />;
            case ShipmentActionType.Origin:
                return <ActionContentRouteStart
                    originStop={itm as StartShipmentAction}
                />;
            case ShipmentActionType.Destination:
                return <ActionContentRouteEnd
                    endStop={itm as EndShipmentAction}
                />;
            case ShipmentActionType.Backhaul:
                return <ActionContentBackHaul
                    key={itm.id}
                    backhaul={itm as BackhaulShipmentAction}
                    routeStatus={props.route.status}
                    onDeleteBackhaul={() => deleteStop(itm)}
                    onStartEdit={showEditBackhaulFlyout}
                />;
            default:
                return null;
        }
    };

    return <React.Fragment>
        <div className="row">
            <div className="col-md-12">
                <div className="pageaction-heading route-list-heading">
                    <h2>Route Information</h2>
                    {
                        (props.route.status !== RouteStatus.Closed) &&
                            (props.route.status !== RouteStatus.Cancelled)
                            ? <HasPermission requiredPermissions={[
                                Permissions.ShippingTrucksEdit, Permissions.ShippingContainersEdit
                            ]}>
                                <ClickAwayListener
                                    key="routelist-actions-clickaway-1"
                                    onClickAway={hideActionsList}>
                                    <span className="combo-create-widget">
                                        <button
                                            className="btn"
                                            onClick={toggleActionsList}>
                                            Actions
                                            <i className="la la-angle-down" />
                                        </button>
                                        {
                                            isActionsOpen
                                                ? getActionList()
                                                : null
                                        }
                                    </span>
                                </ClickAwayListener>
                            </HasPermission>
                            : null
                    }
                </div>
            </div>
        </div>
        <div className="row">
            <div className="col-md-12">
                {
                    props.route.actions && props.route.actions.length > 0
                        ? <div className="truck-stop-route-tab-wrapper">
                            <Tabs
                                orientation="vertical"
                                value={selectedRoute}
                                onChange={onSelectedRouteChange}
                                data-testid="truck-stop-route-tab-list"
                                classes={{ flexContainer: 'truck-stop-route-tab-list' }}
                                aria-label="Route Stops">
                                {
                                    props.route.actions.map((itm: ShipmentAction, idx: number) =>
                                        <Tab
                                            classes={{ root: 'truck-stop-route-tab-item' }}
                                            key={`tab-itm-${idx}`}
                                            data-testid="route-list-tabs"
                                            label={
                                                getTabContent(itm, idx)
                                            }
                                        />
                                    )
                                }
                            </Tabs>
                            {
                                props.route.actions.map((itm: ShipmentAction, idx: number) => {
                                    return <TabPanel
                                        key={`tab-panel-${idx}`}
                                        value={selectedRoute}
                                        index={idx}>
                                        {getTabPanelContent(itm, idx)}
                                    </TabPanel>;
                                })
                            }
                        </div>
                        : <PlaceholderBlock
                            icon="la-exclamation-triangle"
                            message="No Deliveries Added Yet" />
                }
            </div>
        </div>

                <ActionAddActivity
                    visible={ isAddingActivity }
                    onClose={ hideAddActivityFlyout }
                    onSuccess={ onAddActivitySuccess }
                    truckId={props.route.id}
                    serviceCenterId={props.route.serviceCenterId}
                    truckVersion={ props.route.rowVersion }
                    truckTotals={ truckTotals }
                    entityType={ props.route.type }
                    shipmentStatus={ props.route.status }
                />

        <ActionSetSchedule
            visible={isSettingSchedule}
            onClose={hideScheduleFlyout}
            onSuccess={onSetScheduleSuccess}
            entityId={props.route.id}
            entityVersion={props.route.rowVersion}
            entityType={props.route.type}
            stopList={editingStopList}
        />

        <ActionSetCOD
            visible={isSettingCOD}
            onClose={hideCODFlyout}
            onSuccess={onSetCODAmount}
            truckId={props.route.id}
            entityType={props.route.type}
            truckVersion={props.route.rowVersion}
            stopList={editingStopList}
        />

        <ActionEditCustomerDelivery
            visible={isEditingDelivery}
            onClose={hideEditDeliveryFlyout}
            onSuccess={onEditStopSuccess}
            truckId={props.route.id}
            entityType={props.route.type}
            truckVersion={props.route.rowVersion}
            stop={editingDelivery}
            truckTotals={truckTotals}
            shipmentStatus={props.route.status}
            serviceCenterId={props.route.serviceCenterId}
        />

        <ActionEditDeliveryAddress
            visible={isEditingDeliveryAddress}
            onClose={hideEditDeliveryAddressFlyout}
            beforeSave={onEditDeliveryAddressBeforeSave}
            onSuccess={onEditDeliveryAddressSuccess}
            truckId={props.route.id}
            entityType={props.route.type}
            truckVersion={props.route.rowVersion}
            stop={editingDelivery}
        />

        <ActionAddAddon
            visible={isAddingAddon}
            onClose={hideAddAddonFlyout}
            onSuccess={onAddAddonSuccess}
            truckId={props.route.id}
            entityType={props.route.type}
            truckVersion={props.route.rowVersion}
            stop={editingDelivery}
            truckTotals={truckTotals}
        />

        <ActionAddBackorder
            visible={isAddingBackorder}
            onClose={hideAddBackorderFlyout}
            onSuccess={onAddBackorderSuccess}
            truckId={props.route.id}
            truckVersion={props.route.rowVersion}
            stop={editingDelivery}
        />

        <BackorderStatusFlyout
            isOpen={isBackorderInProgress}
            onClose={hideAddBackorderProgressFlyout}
            orders={backorderIds}
        />

        <ActionEditBackhaul
            visible={isEditingBackhaul}
            onClose={hideEditBackhaulFlyout}
            onSuccess={onEditBackhaulSuccess}
            truckId={props.route.id}
            entityType={props.route.type}
            truckVersion={props.route.rowVersion}
            stop={editingBackhaul}
        />

        <ActionSetTruckCustoms
            visible={isSettingCustoms}
            onSuccess={onSetCustomsSuccess}
            onClose={hideCustomsFlyout}
            truck={editingTruck}
        />

    </React.Fragment>;
};
